import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SettingsContext = createContext();

// 기본 설정값
const defaultSettings = {
  // 거래 전략
  strategy: {
    entrySignal: 'RSI',
    entryThreshold: 30,
    confirmationPeriod: 3,
    exitSignal: 'RSI',
    exitThreshold: 70,
    trailingStop: false,
    trailingStopPercent: 2.0,
    positionSizing: 'fixed',
    orderType: 'market',
    slippage: 0.1,
  },

  // 리스크 관리
  risk: {
    stopLossPercent: 2.0,
    maxDailyLoss: 500000,
    maxDailyLossPercent: 5.0,
    takeProfitPercent: 4.0,
    maxDailyProfit: 1000000,
    stopOnTargetReached: false,
    maxPositionSize: 10,
    maxTradeAmount: 2000000,
    maxLeverage: 1,
  },

  // 알림
  notifications: {
    notifyOnOrder: true,
    notifyOnEntry: true,
    notifyOnExit: true,
    notifyOnProfit: true,
    notifyOnLoss: true,
    profitThreshold: 100000,
    lossThreshold: 50000,
    notifyOnError: true,
    notifyOnApiDisconnect: true,
    notifyOnDailyTarget: true,
    notificationChannel: 'browser',
    notificationSound: true,
  },

  // API 연결
  api: {
    apiKey: '',
    apiSecret: '',
    apiProvider: 'mock',
    apiMode: 'simulation',
    autoReconnect: true,
    reconnectDelay: 5,
  },
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // localStorage에서 설정 불러오기
    const savedSettings = localStorage.getItem('tradingSettings');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // 설정 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('tradingSettings', JSON.stringify(settings));
  }, [settings]);

  // 설정 업데이트 함수
  const updateSettings = useCallback((category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  }, []);

  // 전체 카테고리 업데이트
  const updateCategory = useCallback((category, values) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...values,
      },
    }));
  }, []);

  // 설정 초기화
  const resetSettings = useCallback(() => {
    setSettings(defaultSettings);
    localStorage.removeItem('tradingSettings');
  }, []);

  // 프리셋 적용
  const applyPreset = useCallback((presetName) => {
    const presets = {
      conservative: {
        risk: {
          ...defaultSettings.risk,
          stopLossPercent: 1.5,
          takeProfitPercent: 3.0,
          maxDailyLoss: 300000,
          maxPositionSize: 5,
          maxTradeAmount: 1000000,
        },
        strategy: {
          ...defaultSettings.strategy,
          trailingStop: true,
        },
      },
      balanced: defaultSettings,
      aggressive: {
        risk: {
          ...defaultSettings.risk,
          stopLossPercent: 3.0,
          takeProfitPercent: 6.0,
          maxDailyLoss: 1000000,
          maxPositionSize: 20,
          maxTradeAmount: 5000000,
        },
        strategy: {
          ...defaultSettings.strategy,
          trailingStop: false,
        },
      },
    };

    const preset = presets[presetName];
    if (preset) {
      setSettings(prev => ({
        ...prev,
        ...preset,
      }));
    }
  }, []);

  const value = {
    settings,
    updateSettings,
    updateCategory,
    resetSettings,
    applyPreset,
    defaultSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
